import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Mail, Phone, MapPin, Send, Youtube, Instagram, Linkedin } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/context/LanguageContext';
import TikTokIcon from '@/components/icons/TikTokIcon';

// CRITICAL SECURITY: Strict input validation
const formSchema = z.object({
  name: z.string()
    .trim()
    .min(2, { message: "Name must be at least 2 characters." })
    .max(100, { message: "Name must be less than 100 characters." }),
  email: z.string()
    .trim()
    .email({ message: "Please enter a valid email address." })
    .max(255, { message: "Email must be less than 255 characters." }),
  subject: z.string()
    .trim()
    .min(5, { message: "Subject must be at least 5 characters." })
    .max(200, { message: "Subject must be less than 200 characters." }),
  message: z.string()
    .trim()
    .min(10, { message: "Message must be at least 10 characters." })
    .max(2000, { message: "Message must be less than 2000 characters." }),
});

type FormValues = z.infer<typeof formSchema>;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    
    try {
      // CRITICAL SECURITY: Sanitize and encode data for WhatsApp
      // Never log sensitive user data
      const message = encodeURIComponent(
        `*New Contact Message*\n\n` +
        `*Name:* ${data.name}\n` +
        `*Email:* ${data.email}\n` +
        `*Subject:* ${data.subject}\n\n` +
        `*Message:*\n${data.message}`
      );
      
      // Send via WhatsApp (replace with your actual number)
      const whatsappUrl = `https://wa.me/34912345678?text=${message}`;
      window.open(whatsappUrl, '_blank');
      
      toast({
        title: "Message sent!",
        description: "We'll get back to you as soon as possible.",
      });
      
      form.reset();
    } catch (error) {
      // Don't log user data in production
      console.error("Form submission failed");
      toast({
        title: "Something went wrong",
        description: "Your message couldn't be sent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-brand-gray-900 mb-8 text-center">
            {t('contactUs')}
          </h1>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-brand-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-brand-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-brand-gray-800 mb-2">{t('email')}</h3>
                <a href="mailto:info@psychotherapist.help" className="text-brand-blue-500 hover:underline">
                  info@psychotherapist.help
                </a>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-brand-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Phone className="h-6 w-6 text-brand-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-brand-gray-800 mb-2">{t('phone')}</h3>
                <a href="tel:+34912345678" className="text-brand-blue-500 hover:underline">
                  +34 91 234 5678
                </a>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-brand-blue-100 rounded-full flex items-center justify-center mb-4">
                  <MapPin className="h-6 w-6 text-brand-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-brand-gray-800 mb-2">{t('address')}</h3>
                <p className="text-brand-gray-600">
                  Calle Gran Vía 123<br />
                  28013 Madrid, Spain
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-brand-gray-900 mb-6">
              Follow Us
            </h2>
            <div className="flex gap-6">
              <a 
                href="https://www.youtube.com/@FormacionPsicoterapia"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-brand-gray-600 hover:text-brand-blue-500 transition-colors"
              >
                <Youtube className="h-6 w-6 mr-2" />
                <span>YouTube</span>
              </a>
              <a 
                href="https://www.instagram.com/formacionpsicoterapia/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-brand-gray-600 hover:text-brand-blue-500 transition-colors"
              >
                <Instagram className="h-6 w-6 mr-2" />
                <span>Instagram</span>
              </a>
              <a 
                href="https://www.linkedin.com/company/formacion-psicoterapia/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-brand-gray-600 hover:text-brand-blue-500 transition-colors"
              >
                <Linkedin className="h-6 w-6 mr-2" />
                <span>LinkedIn</span>
              </a>
              <a 
                href="https://www.tiktok.com/@formacionpsicoterapia"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-brand-gray-600 hover:text-brand-blue-500 transition-colors"
              >
                <TikTokIcon className="h-6 w-6 mr-2" />
                <span>TikTok</span>
              </a>
            </div>
          </div>
          
          <Card className="shadow-md mb-12">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-2xl font-semibold text-brand-gray-800 mb-6">
                {t('sendUsMessage')}
              </h2>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('yourName')}</FormLabel>
                          <FormControl>
                            <Input placeholder="John Doe" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('yourEmail')}</FormLabel>
                          <FormControl>
                            <Input placeholder="you@example.com" type="email" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('subject')}</FormLabel>
                        <FormControl>
                          <Input placeholder="How can we help you?" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('message')}</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Please provide details about your inquiry..."
                            className="min-h-32"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full md:w-auto bg-brand-blue-500 hover:bg-brand-blue-600"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('sending')}
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Send className="mr-2 h-4 w-4" />
                        {t('sendMessage')}
                      </span>
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
          
          <div className="rounded-xl overflow-hidden shadow-md h-80">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3037.6174376053986!2d-3.7037075!3d40.4200528!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd42288d1ecb8515%3A0x61c4d9352ef08f3b!2sGran%20V%C3%ADa%2C%20Madrid%2C%20Spain!5e0!3m2!1sen!2sus!4v1617234568745!5m2!1sen!2sus" 
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              allowFullScreen 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Office Location"
            ></iframe>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
